import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-24">
      <SEOHead path="/om-os" />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Om Dilling EL
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] mt-6 leading-[1.1]">
                Specialister i energioptimering af erhvervsbygninger
              </h1>
              <p className="text-[#1E2A24]/70 text-lg mt-6 leading-[1.8]">
                Hos Dilling EL arbejder vi med energioptimering og intelligent belysning i 
                erhvervsbygninger, hvor belysning ofte udgør en væsentlig del af energiforbruget.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img loading="lazy" 
                  src="/images/film-grabs/still-7.jpg" 
                  alt="Dilling EL team"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Indikatorer */}
      <section className="py-16 md:py-20 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-4 text-center"
          >
            {[
              { value: '20+', label: 'Års erfaring' },
              { value: 'Før / efter', label: 'Energimålinger' },
              { value: 'Intelligent', label: 'Lysstyring' },
              { value: 'Driftssikker', label: 'Implementering' },
              { value: 'Dokumenterbar', label: 'ESG-effekt' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-medium text-white">
                  {item.value}
                </div>
                <p className="text-white/50 text-sm mt-1">{item.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vores historie */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
              Vores historie
            </span>
            <div className="mt-8 space-y-6 text-[#1E2A24]/70 leading-[1.8]">
              <p>
                Vi hjælper virksomheder med at reducere energiforbrug, forbedre lysforhold og 
                skabe mere effektive installationer gennem moderne LED-løsninger og intelligent styring.
              </p>
              <p>
                Effekten dokumenteres gennem før- og eftermålinger, så virksomheden får 
                konkrete data på energibesparelse og effektreduktion.
              </p>
              <p>
                Vi samarbejder tæt med drift, facility management, økonomi og ESG-ansvarlige, 
                som har brug for gennemsigtige løsninger og dokumentation der kan bruges internt 
                og i rapportering.
              </p>
              <p>
                Vores arbejde bygger på faste teams, tydelig planlægning og driftssikre løsninger, 
                der kan implementeres uden unødige forstyrrelser af den daglige drift.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white mb-8">
              Lad os tale om jeres projekt
            </h2>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase bg-white text-[#0E3D24] px-8 py-4 hover:bg-[#F4F2EE] transition-colors group"
            >
              <span>Kontakt os</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
